/**
 * Конфигурация центрального барабана и статичных кругов
 * Барабан крутится, в центре виден один элемент. Результат спина = этот элемент.
 * Статичные круги (красный, зелёный, синий) заполняются при выпадении соответствующего цвета.
 */

export type DrumSegmentType = 'red' | 'green' | 'blue' | 'gray' | 'skull';

export interface DrumSegmentConfig {
  type: DrumSegmentType;
  value?: number; // множитель X для red/green/blue
}

/** Сегменты центрального барабана: цвета с X, серый, череп */
export const DRUM_SEGMENTS: DrumSegmentConfig[] = [
  { type: 'red', value: 2.5 },
  { type: 'green', value: 3 },
  { type: 'blue', value: 1.5 },
  { type: 'gray' },
  { type: 'red', value: 5 },
  { type: 'skull' },
  { type: 'green', value: 7 },
  { type: 'blue', value: 2 },
  { type: 'gray' },
  { type: 'red', value: 1.5 },
  { type: 'green', value: 4 },
  { type: 'blue', value: 6 },
  { type: 'skull' },
  { type: 'gray' },
];

/** Радиус центрального барабана (внутренний элемент), масштаб 2x */
export const DRUM_RADIUS = 160;

/** Радиусы статичных кругов (внешний край каждого кольца) */
export const STATIC_RING_RADII = {
  blue: 240,
  green: 318,
  red: 396,
} as const;

/** Ширина кольца (толщина) — уже чем раньше, как в референсе */
export const RING_WIDTH = 68;

/** Множители в порядке заполнения (RAW) - используются для логики выплат */
// Изначально отсортированы по возрастанию для отображения на сегментах
// Красный: 8 сегментов, последний BONUS (значение не используется для вывода)
const RED_RAW = [3.9, 12.5, 28, 52, 85, 133, 200, 0];
// Зеленый: 6 сегментов, последний +20.5X
const GREEN_RAW = [2.5, 7.7, 16, 27.5, 44, 20.5];
// Синий: 4 сегмента, последний +7X
const BLUE_RAW = [1.55, 4.85, 10, 7];

/** RAW множители для логики заполнения (в порядке заполнения) */
export const RING_MULTIPLIERS_RAW: Record<RingColor, number[]> = {
  red: RED_RAW,
  green: GREEN_RAW,
  blue: BLUE_RAW,
};

/** Множители для отображения на сегментах (уже отсортированы) */
export const RING_MULTIPLIERS: Record<RingColor, number[]> = {
  red: RED_RAW,
  green: GREEN_RAW,
  blue: BLUE_RAW,
};

export type RingColor = 'red' | 'green' | 'blue';

/**
 * Последняя секция окружности — особая логика:
 * - blue (самая простая, 4 секции): +x7 к ставке, авто-начисление и снятие окрашивания
 * - green (средняя, 6 секций): +x20 к ставке, авто-начисление и снятие окрашивания
 * - red (огненные секции, 8): BONUS — бонусная игра при заполнении всех огненных секций
 */
export const LAST_SEGMENT_BONUS: Record<RingColor, number | 'BONUS'> = {
  blue: 7,
  green: 20.5,
  red: 'BONUS',
};

/** Множители бонусной игры (x100, x200, x300, x400, x500) — равный шанс выпадения */
export const BONUS_GAME_MULTIPLIERS = [100, 200, 300, 400, 500] as const;

/** Угол индикатора: сверху = 0 (или -90 в градусах от правой оси) */
export const INDICATOR_ANGLE_RAD = -Math.PI / 2;
